/**
 * FRONT-2598 — which slide-strip buttons are available, and why not.
 *
 * ONE decision per button: it is disabled iff this returns a sentence, and the
 * sentence is what the button's tooltip says, so a fifth reason cannot be added
 * without its words. (CLAUDE.md: a disabled control and its explanation are one
 * decision.)
 */

export type StripAction = 'moveUp' | 'moveDown' | 'duplicate' | 'delete';

export const STRIP_ACTION_LABEL: Record<StripAction, string> = {
  moveUp: 'Move up',
  moveDown: 'Move down',
  duplicate: 'Duplicate slide',
  delete: 'Delete slide',
};

export const STRIP_ACTIONS: readonly StripAction[] = ['moveUp', 'moveDown', 'duplicate', 'delete'];

/**
 * Why `action` on the slide at `index` is unavailable, or null when it is available.
 *
 *  - `frozenReason` (a save is running, or the draft is blocked) outranks everything: the
 *    whole arrangement is locked, and the sentence says why.
 *  - `maxSlides` is the server's own ceiling, sent with the deck (`GET /api/artifacts/deck/:id`);
 *    until it is known, duplicating is not gated here and the server refuses with its own sentence.
 */
export function stripActionReason(
  action: StripAction,
  index: number,
  count: number,
  maxSlides: number | undefined,
  frozenReason: string | null = null,
): string | null {
  if (frozenReason) return frozenReason;
  switch (action) {
    case 'moveUp':
      return index <= 0 ? "This is the first slide, it can't move up." : null;
    case 'moveDown':
      return index >= count - 1 ? "This is the last slide, it can't move down." : null;
    case 'duplicate':
      return maxSlides !== undefined && count >= maxSlides ? `A deck can have at most ${maxSlides} slides.` : null;
    case 'delete':
      return count <= 1 ? "A deck needs at least one slide, so the last one can't be deleted." : null;
  }
}

export const SAVING_REASON = 'Saving…';
